import { useState } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import { ReactComponent as AlertIcon } from 'src/assets/icons/alert.svg';
import { deleteCampaign } from 'src/apis/campaign';
import useNotification from 'src/hooks/useNotification';
import { CampaignProps } from 'src/types/campaign';

interface DeleteCampaignModalProps {
  open: boolean;
  campaign: CampaignProps | null;
  closeModal: () => void;
  onDeleted: (campaignId: string) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    warning: {
      borderRadius: 28,
      border: '8px solid #FFFAEB',
      background: '#FEF0C7',
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      marginTop: 16,
      marginBottom: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    buttons: {
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      width: '100%',
      '& button': {
        flex: 1,
      },
    },
  })
);

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 400,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

export const DeleteCampaignModal = ({
  open,
  campaign,
  closeModal,
  onDeleted,
}: DeleteCampaignModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleError, handleSuccess } = useNotification();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!campaign) {
      return;
    }

    setLoading(true);
    try {
      await deleteCampaign(campaign.id);
      handleSuccess('Campaign deleted successfully');
      onDeleted(campaign.id);
      closeModal();
    } catch (error) {
      handleError(error, 'There was an error while deleting the campaign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : closeModal}
      aria-labelledby='delete-campaign-modal-title'
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 800,
      }}
    >
      <Box sx={{ ...modalStyle }}>
        <Box className={classes.header}>
          <Box className={classes.warning}>
            <AlertIcon />
          </Box>
          <CancelIcon
            cursor={loading ? 'default' : 'pointer'}
            width={24}
            height={24}
            onClick={loading ? undefined : closeModal}
          />
        </Box>
        <Box className={classes.content}>
          <Typography
            className={textClasses.lgBold}
            style={{ color: '#101828' }}
          >
            Delete Campaign
          </Typography>
          <Typography
            className={textClasses.smRegular}
            style={{ color: '#667085' }}
          >
            Are you sure you want to delete &quot;{campaign?.name}&quot;? This
            action cannot be undone.
          </Typography>
        </Box>
        <Box className={classes.buttons}>
          <Button
            title='Cancel'
            variant='outlined'
            height={46}
            onClick={closeModal}
            disabled={loading}
          />
          <Button
            height={46}
            onClick={handleConfirm}
            disabled={loading}
            style={{ backgroundColor: '#D92D20' }}
          >
            {loading ? (
              <CircularProgress style={{ color: '#fff' }} size={24} />
            ) : (
              <span>Delete</span>
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
